import type { CanonicalName } from "../interfaces/payout";

/**
 * Recipient book shapes — gallery-safe mirror of flow-send
 * `GET /widget/links/:linkId/recipients`. Data comes from `sendMock`.
 */

export interface RecipientDestinationCash {
  stateCode?: string;
  payerCode?: string;
}

export interface RecipientDestinationDisplay {
  bankName?: string;
  maskedAccount?: string;
  cardBrand?: string;
  cardLast4?: string;
}

export interface RecipientDestination {
  payoutMethod: string;
  cash?: RecipientDestinationCash;
  paymentMethodId?: string;
  display?: RecipientDestinationDisplay;
  phone?: string;
  lastCapabilityKey?: string;
  lastUsedAt: string;
  timesUsed: number;
}

export interface RecipientBookEntry {
  recipientKey: string;
  name: CanonicalName;
  address?: {
    countryISO2?: string;
    state?: string;
    city?: string;
    street?: string;
    postalCode?: string;
  };
  phone?: string;
  /** Optional DOB from the latest operation for this fingerprint (YYYY-MM-DD). */
  dateOfBirth?: string;
  lastUsedAt: string;
  timesUsed: number;
  destinations: RecipientDestination[];
}

/** Joins a `CanonicalName` into the display string (LatAm order). */
export const formatCanonicalName = (name: CanonicalName): string =>
  [name.firstName, name.middleName, name.lastName, name.secondLastName]
    .filter((part): part is string => Boolean(part?.trim()))
    .join(" ");

/**
 * Conservative positional LatAm split (gallery has no parse/name API).
 *
 *   2 tokens → first + last
 *   3 tokens → first + last + secondLast
 *   4+ tokens → first + middle(s) + last + secondLast
 */
export const fallbackCanonicalName = (
  fullNameText: string
): CanonicalName | null => {
  const tokens = fullNameText.trim().split(/\s+/).filter(Boolean);
  if (tokens.length < 2) return null;
  if (tokens.length === 2) {
    return { firstName: tokens[0], lastName: tokens[1] };
  }
  if (tokens.length === 3) {
    return {
      firstName: tokens[0],
      lastName: tokens[1],
      secondLastName: tokens[2],
    };
  }
  return {
    firstName: tokens[0],
    middleName: tokens.slice(1, -2).join(" "),
    lastName: tokens[tokens.length - 2],
    secondLastName: tokens[tokens.length - 1],
  };
};
